// Ultimate Card Game (UCG) - Bot AI humanizer
//
// Changes WHEN and HOW PRECISELY the bot acts, never the rules: a bot that
// reacts to what it saw a moment ago, clicks in bursts and pauses instead of
// on a clock, thinks twice before an expensive card or the superweapon, gets
// rattled by setbacks and (Easy/Normal only) now and then picks the second-best
// card. Everything is drawn from the bot's own rng (docs/AI_MASTERPLAN_2026-10.md, 6.6).
//
// The one rule that keeps it honest: PERCEPTION is delayed, OUTPUT is not.
// Plasma, the hand and every card the bot plays are always current; only what
// the bot believes about the enemy's board is a little old. A reaction delay
// that is implemented as "do nothing for a moment" makes a bot save plasma by
// accident and play stronger (the September measurement); here a late
// reaction only shifts WHICH posture the bot is in, and the measurements
// (npm run ai:eval -- human, the ladder) show it does not move the ladder.
//
// Config (profile.humanizer; absent/null = off, the bot behaves as before).
// Every part is optional:
//   latency      { median, sigma }  seconds the bot's picture of the enemy is
//                                   behind (log-normal per tick)
//   attention    0.5 .. 1           low-salience things (cloak, structures,
//                                   artillery) are noticed after median/attention
//   rhythm       { cv, burstP, burstGap, spread, followUp: [min, max] }
//                                   gamma-distributed gaps between decisions
//                                   (mean unchanged) with short burst gaps;
//                                   `spread` plays one card per decision and
//                                   follows up after `followUp` seconds
//   hesitation   { minCost, range: [min, max], nuke }  a pause before an
//                                   expensive card / a value strike
//   mood         { susceptibility, gain, halfLife }  confidence in [-1, 1];
//                                   > 0 tilts after a setback (rash), < 0
//                                   stays calm (consolidates)
//   errors       { tau, slipRate, slipSeconds }  softmax over the best three
//                                   cards (never over a gated one) and a
//                                   forgotten draw
//
// Reads only what the bot may know; never touches the sim.

import { gamma, lognormal } from './sampling.js';

export const HUMANIZER_DEFAULTS = Object.freeze({
  latency: Object.freeze({ median: 0.5, sigma: 0.5 }),
  attention: 1,
  rhythm: Object.freeze({ cv: 0.6, burstP: 0.25, burstGap: 0.3, spread: false, followUp: Object.freeze([0.15, 0.4]) }),
  hesitation: Object.freeze({ minCost: 30, range: Object.freeze([0.3, 1.2]), nuke: true }),
  mood: Object.freeze({ susceptibility: 0, gain: 0.15, halfLife: 40 }),
  errors: Object.freeze({ tau: 0, slipRate: 0, slipSeconds: Object.freeze([2, 6]) }),
  variance: Object.freeze({ tau: 0 })
});

const PARTS = Object.keys(HUMANIZER_DEFAULTS);

// Fields of the enemy-board scan that are a reaction (lagged), grouped by how
// easy they are to notice. Numbers are interpolated, flags taken from the
// nearer snapshot.
const TACTICAL_FIELDS = ['enemyValue', 'ownValue', 'enemyInOwnHalf', 'ownInOwnHalf', 'enemyNearBase', 'ownNearBase', 'enemyParachutingCount', 'enemyParachutingValue'];
const THREAT_FIELDS = [
  'enemyAirUnits', 'enemyArmoredUnits', 'enemyInfantryCount', 'enemyGroundCount', 'enemyParachutingCount',
  'hasEnemyParachuting', 'enemyNearBaseCount', 'isPressedBack', 'enemyLethalGlassCannons', 'recentAir', 'recentArmor',
  'enemyStructuresCount', 'enemyArtilleryCount', 'hasEnemySiegeLine', 'enemyHasCloakedThreats', 'recentCloak'
];
// Things that are easy to overlook: a hidden cloaker, a quiet siege line.
const LOW_SALIENCE = new Set(['enemyStructuresCount', 'enemyArtilleryCount', 'hasEnemySiegeLine', 'enemyHasCloakedThreats', 'recentCloak']);

const HISTORY_SECONDS = 8;
const MIN_INTERVAL = 0.1;
const MAX_SPAN = 4;

function merge(defaults, given) {
  if (given === false || given === null || given === undefined) return null;
  return { ...defaults, ...(given === true ? {} : given) };
}

// The bot's picture of the enemy as it was `lag` seconds ago.
class LaggedView {
  constructor(view, tactical) {
    this.bot = view.bot;
    this._tactical = tactical;
  }

  get tactical() {
    return this._tactical;
  }
}

export class Humanizer {
  constructor(bot) {
    this.bot = bot;
    this.config = null;
    this.reset();
  }

  setConfig(config) {
    if (!config) {
      this.config = null;
    } else {
      const given = config === true ? Object.fromEntries(PARTS.map(p => [p, true])) : config;
      const merged = {};
      for (const part of PARTS) {
        merged[part] = part === 'attention'
          ? (typeof given.attention === 'number' ? given.attention : HUMANIZER_DEFAULTS.attention)
          : merge(HUMANIZER_DEFAULTS[part], given[part]);
      }
      this.config = merged;
    }
    this.reset();
  }

  get active() {
    return Boolean(this.config);
  }

  reset() {
    this.history = [];
    this._threatHistory = [];
    this.confidence = 0;
    this.lastT = null;
    this.lastHp = null;
    this.hesitations = new Map();
    this.nukeFireAt = null;
    this.slipUntil = 0;
    this.followUp = false;
    this.lastLag = 0;
    this.stats = { ticks: 0, lagSum: 0, lagSqSum: 0, hesitations: 0, nukeHesitations: 0, slips: 0, forgottenDraws: 0, minConfidence: 0, maxConfidence: 0 };
  }

  // --- per-tick bookkeeping ---------------------------------------------------

  // Call at the top of a decision tick with the fresh BoardView.
  observe(view) {
    if (!this.config) return;
    const t = this.bot.gameTime;
    const dt = this.lastT === null ? 0 : Math.max(0, Math.min(2, t - this.lastT));
    this.lastT = t;
    this.stats.ticks++;
    if (this.config.latency) {
      const tac = view.tactical;
      const snap = { t, tac: {} };
      for (const f of TACTICAL_FIELDS) snap.tac[f] = tac[f];
      this.history.push(snap);
      while (this.history.length > 2 && t - this.history[0].t > HISTORY_SECONDS) this.history.shift();
    }
    if (this.config.mood) this._updateMood(view, dt);
  }

  _updateMood(view, dt) {
    const m = this.config.mood;
    const base = this.bot.battlefield?.bases?.[this.bot.team];
    const hp = base ? base.hp / (base.maxHp || 1) : 1;
    let c = this.confidence;
    if (this.lastHp !== null) c += (hp - this.lastHp) * 4;
    this.lastHp = hp;
    c *= Math.pow(0.5, dt / (m.halfLife || 40));
    const tac = view.tactical;
    const total = tac.ownValue + tac.enemyValue;
    if (total > 30 && dt > 0) {
      const target = (tac.ownValue / total - 0.5) * 1.2;
      c += (target - c) * Math.min(1, dt / 25) * 0.5;
    }
    this.confidence = Math.max(-1, Math.min(1, c));
    this.stats.minConfidence = Math.min(this.stats.minConfidence, this.confidence);
    this.stats.maxConfidence = Math.max(this.stats.maxConfidence, this.confidence);
  }

  // --- perception latency -----------------------------------------------------

  // The seconds this tick's reactions are behind: log-normal around the tier's median.
  _drawLag(extra = 1) {
    const l = this.config.latency;
    if (!l) return 0;
    const lag = Math.min(l.median * 3, lognormal(l.median, l.sigma, this.bot.rng)) * extra;
    return Math.max(0, lag);
  }

  _snapshotAt(target) {
    const h = this.history;
    if (h.length === 0) return null;
    if (target <= h[0].t) return { a: h[0], b: h[0], w: 0 };
    for (let i = h.length - 1; i > 0; i--) {
      if (h[i - 1].t <= target) {
        const a = h[i - 1];
        const b = h[i];
        const span = b.t - a.t;
        return { a, b, w: span > 0 ? Math.min(1, (target - a.t) / span) : 0 };
      }
    }
    return { a: h[0], b: h[0], w: 0 };
  }

  // A view whose enemy-board figures are `lag` seconds old. Plasma and the
  // bot's own state are not part of it. Returns the fresh view when latency is off.
  reactionView(view) {
    if (!this.config?.latency || this.history.length < 2) return view;
    const lag = this._drawLag();
    this.lastLag = lag;
    this.stats.lagSum += lag;
    this.stats.lagSqSum += lag * lag;
    const snap = this._snapshotAt(this.lastT - lag);
    if (!snap) return view;
    const fresh = view.tactical;
    const tactical = { ...fresh };
    for (const f of TACTICAL_FIELDS) {
      tactical[f] = snap.a.tac[f] + (snap.b.tac[f] - snap.a.tac[f]) * snap.w;
    }
    return new LaggedView(view, tactical);
  }

  // The hand scorer's threat summary with the enemy's counts as they were a
  // moment ago; the unit lists, the bot's own anti-air and everything the bot
  // controls stay current. Records the fresh figures for later ticks.
  reactionThreats(threats) {
    if (!this.config?.latency) return threats;
    const t = this.lastT ?? this.bot.gameTime;
    const snap = { t, fields: {} };
    for (const f of THREAT_FIELDS) snap.fields[f] = threats[f];
    const h = this._threatHistory;
    h.push(snap);
    while (h.length > 2 && t - h[0].t > HISTORY_SECONDS) h.shift();
    if (h.length < 2) return threats;
    const attention = Math.max(0.3, this.config.attention ?? 1);
    const out = { ...threats };
    const lag = this.lastLag;
    for (const f of THREAT_FIELDS) {
      const target = t - (LOW_SALIENCE.has(f) ? lag / attention : lag);
      let a = h[0];
      let b = h[0];
      for (let i = h.length - 2; i >= 0; i--) {
        if (h[i].t <= target) { a = h[i]; b = h[i + 1]; break; }
      }
      // The snapshot just recorded is the newest; interpolate numbers, take the nearer flag.
      const va = a.fields[f];
      const vb = b.fields[f];
      if (typeof va === 'number') {
        const span = b.t - a.t;
        const w = span > 0 ? Math.max(0, Math.min(1, (target - a.t) / span)) : 0;
        out[f] = Math.round(va + (vb - va) * w);
      } else {
        out[f] = (target - a.t) <= (b.t - target) ? va : vb;
      }
    }
    return out;
  }

  // --- rhythm -------------------------------------------------------------------

  // Seconds until the next decision. `base` is the tier's own mean interval.
  nextInterval(base) {
    const r = this.config?.rhythm;
    if (!r) return base;
    const rng = this.bot.rng;
    if (this.followUp && r.spread) {
      this.followUp = false;
      const [lo, hi] = r.followUp;
      return Math.max(MIN_INTERVAL, lo + rng.next() * (hi - lo));
    }
    this.followUp = false;
    const p = r.burstP;
    const g = r.burstGap;
    if (rng.next() < p) return Math.max(MIN_INTERVAL, base * g * (0.7 + 0.6 * rng.next()));
    // The long gaps carry the rest of the mean, so decisions per second stay what the tier says.
    const meanLong = base * (1 - p * g) / (1 - p);
    const k = Math.max(1, 1 / (r.cv * r.cv));
    return Math.max(MIN_INTERVAL, meanLong * gamma(k, rng) / k);
  }

  // The tick wants another decision soon (it played one card and could play more).
  requestFollowUp() {
    if (this.config?.rhythm?.spread) this.followUp = true;
  }

  get spread() {
    return Boolean(this.config?.rhythm?.spread);
  }

  // --- hesitation ---------------------------------------------------------------

  // Should this play wait? True while the bot is still making up its mind about
  // an expensive card. `posture` 'defend' and a flood never hesitate (reflex).
  hesitate(card, slot, posture) {
    const h = this.config?.hesitation;
    if (!h || !card || posture === 'defend') return false;
    if ((card.cost || 0) < h.minCost || card.isSpell) return false;
    const now = this.bot.gameTime;
    const entry = this.hesitations.get(slot);
    if (entry && entry.name === card.name) {
      if (now >= entry.until) return false;
      return true;
    }
    const wait = h.range[0] + this.bot.rng.next() * (h.range[1] - h.range[0]);
    this.hesitations.set(slot, { name: card.name, until: now + wait, wait });
    this.stats.hesitations++;
    return true;
  }

  // The card in this slot was played or left the hand.
  settle(slot) {
    this.hesitations.delete(slot);
  }

  pendingHesitationSeconds(slot) {
    const e = this.hesitations.get(slot);
    return e ? Math.max(0, e.until - this.bot.gameTime) : 0;
  }

  // A value strike waits a moment (a base-saving strike never does).
  nukeHold(critical) {
    const h = this.config?.hesitation;
    if (!h || !h.nuke || critical) return false;
    const now = this.bot.gameTime;
    if (this.nukeFireAt === null) {
      this.nukeFireAt = now + h.range[0] + this.bot.rng.next() * (h.range[1] - h.range[0]);
      this.stats.nukeHesitations++;
    }
    return now < this.nukeFireAt;
  }

  nukeIdle() {
    this.nukeFireAt = null;
  }

  // --- mood ---------------------------------------------------------------------

  // Multipliers for the wave planner (merged with the strategist's by BotAI.waveMods()).
  waveMods() {
    const m = this.config?.mood;
    if (!m || !m.susceptibility) return null;
    const e = m.susceptibility * m.gain * this.confidence;
    if (Math.abs(e) < 0.005) return null;
    return { thresholdMul: 1 + e, defendFactorMul: 1 - e };
  }

  // --- plausible errors -----------------------------------------------------------

  // Softmax over the best three affordable cards. Gated cards (score <= 0)
  // never take part, so no safety rule can be talked out of.
  choose(result) {
    const e = this.config?.errors;
    const v = this.config?.variance;
    const best = result.bestAffordable;
    if (!best) return null;

    // Emergency base defense: 100% deterministic best play
    const isEmergency = Boolean(
      this.bot.threats?.isPressedBack ||
      (this.bot.threats?.enemyNearBaseCount || 0) >= 2
    );
    if (isEmergency) return best;

    const baseTau = (e && e.tau) || (v && v.tau) || 0;
    if (!baseTau) return best;

    const mood = this.config?.mood;
    const tilt = mood && mood.susceptibility > 0 ? 1 + 0.6 * Math.max(0, -this.confidence) * mood.susceptibility : 1;
    const tau = baseTau * tilt;
    const pool = result.candidates.filter(c => c.isAffordable && c.score > 0).slice(0, 3);
    if (pool.length < 2) return best;
    const weights = pool.map(c => Math.exp((c.score - best.score) / tau));
    const total = weights.reduce((a, b) => a + b, 0);
    let roll = this.bot.rng.next() * total;
    for (let i = 0; i < pool.length; i++) {
      roll -= weights[i];
      if (roll <= 0) {
        if (pool[i] !== best) this.stats.slips++;
        return pool[i];
      }
    }
    return best;
  }

  // Forgets to draw for a few seconds, now and then.
  skipDraw() {
    const e = this.config?.errors;
    if (!e || !e.slipRate) return false;
    const now = this.bot.gameTime;
    if (now < this.slipUntil) return true;
    if (this.bot.rng.next() < e.slipRate) {
      this.slipUntil = now + e.slipSeconds[0] + this.bot.rng.next() * (e.slipSeconds[1] - e.slipSeconds[0]);
      this.stats.forgottenDraws++;
      return true;
    }
    return false;
  }
}
